import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import clsx from 'clsx';
import {
  GROUP_TITLE_MAX_LENGTH,
  type CommentView,
  type GroupView,
  type RetroSnapshot,
  type TopicView,
} from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { Link2 } from 'lucide-react';
import { Modal } from '../ui.js';
import { BoardColumn, BoardTrack } from './BoardLayout.js';
import { CommentCard, ReactionBar } from './CommentCard.js';
import { CommentComposer } from './CommentComposer.js';
import { SuggestionCard } from './SuggestionCard.js';

type SortMode = 'NONE' | 'REACTIONS' | 'NEWEST' | 'OLDEST';

const SORT_LABELS: Record<SortMode, string> = {
  NONE: 'No sorting',
  REACTIONS: 'Most reactions',
  NEWEST: 'Newest first',
  OLDEST: 'Oldest first',
};

const dropId = (type: 'COMMENT' | 'GROUP', id: string) => `${type}:${id}`;

function parseDropId(raw: string): { type: 'COMMENT' | 'GROUP'; id: string } | null {
  const [type, ...rest] = raw.split(':');
  const id = rest.join(':');
  if (!id) return null;
  if (type === 'COMMENT' || type === 'GROUP') return { type, id };
  return null;
}

/** A card that can be picked up and dropped onto another card or group. */
function DraggableCard({
  comment,
  topics,
  canGroup,
  canReact,
  dimmed,
  highlighted,
  myName,
  inGroup,
  onHighlightAuthor,
  onGroupWith,
}: {
  comment: CommentView;
  topics: TopicView[];
  canGroup: boolean;
  canReact: boolean;
  dimmed: boolean;
  highlighted: string | null;
  myName: string;
  inGroup: boolean;
  onHighlightAuthor: () => void;
  onGroupWith?: () => void;
}) {
  const draggable = useDraggable({ id: dropId('COMMENT', comment.id), disabled: !canGroup });
  const droppable = useDroppable({ id: dropId('COMMENT', comment.id), disabled: !canGroup });

  return (
    <div
      ref={(node) => {
        draggable.setNodeRef(node);
        droppable.setNodeRef(node);
      }}
      className={clsx(
        'rounded-[10px] transition',
        droppable.isOver && !draggable.isDragging && 'ring-2 ring-brand-500 ring-offset-2',
        draggable.isDragging && 'opacity-40',
      )}
    >
      <CommentCard
        comment={comment}
        topics={topics}
        canReact={canReact}
        canEditTopics={canGroup}
        canUngroup={canGroup && inGroup}
        dimmed={dimmed}
        highlightActive={highlighted === comment.author?.participantId}
        myName={myName}
        onHighlightAuthor={onHighlightAuthor}
        onGroupWith={onGroupWith}
        // Only the pointer listeners. dnd-kit's `attributes` would also stamp
        // role="button" and tabIndex onto the card, which is wrong for a block
        // of text and would advertise keyboard dragging we do not implement —
        // the "Group with…" button is the accessible path instead.
        dragHandleProps={canGroup ? draggable.listeners : undefined}
      />
    </div>
  );
}

/** Choose a grouping target without dragging. */
function GroupWithDialog({
  source,
  candidates,
  groups,
  open,
  onClose,
}: {
  source: CommentView | null;
  candidates: CommentView[];
  groups: GroupView[];
  open: boolean;
  onClose: () => void;
}) {
  const send = useRetroStore((state) => state.send);
  if (!source) return null;

  const choose = (target: { type: 'COMMENT' | 'GROUP'; id: string }) => {
    void send('comment:group', { commentId: source.id, target });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Group this comment with…">
      <p className="mb-3 rounded-[10px] bg-subtle p-3 text-[13px]">{source.text}</p>

      {candidates.length === 0 && groups.length === 0 ? (
        <p className="text-[13px] text-muted">
          There is nothing else in this column to group it with yet.
        </p>
      ) : (
        <ul className="max-h-72 space-y-1.5 overflow-y-auto">
          {groups.map((group) => (
            <li key={group.id}>
              <button
                type="button"
                onClick={() => choose({ type: 'GROUP', id: group.id })}
                className="w-full rounded-[10px] border border-line2 p-3 text-left text-[13px] transition hover:border-brand-500 hover:bg-brand-100"
              >
                <span className="font-semibold">{group.title || 'Grouped comments'}</span>
                <span className="ml-2 text-muted">
                  {group.commentIds.length} comment{group.commentIds.length === 1 ? '' : 's'}
                </span>
              </button>
            </li>
          ))}
          {candidates.map((candidate) => (
            <li key={candidate.id}>
              <button
                type="button"
                onClick={() => choose({ type: 'COMMENT', id: candidate.id })}
                className="w-full rounded-[10px] border border-line2 p-3 text-left text-[13px] transition hover:border-brand-500 hover:bg-brand-100"
              >
                {candidate.text}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function GroupContainer({
  group,
  comments,
  topics,
  canGroup,
  canReact,
  dimmedIds,
  highlighted,
  myName,
  onHighlightAuthor,
  onGroupWith,
}: {
  group: GroupView;
  comments: CommentView[];
  topics: TopicView[];
  canGroup: boolean;
  canReact: boolean;
  dimmedIds: Set<string> | null;
  highlighted: string | null;
  myName: string;
  onHighlightAuthor: (comment: CommentView) => void;
  onGroupWith?: (comment: CommentView) => void;
}) {
  const send = useRetroStore((state) => state.send);
  const droppable = useDroppable({ id: dropId('GROUP', group.id), disabled: !canGroup });

  return (
    <div
      ref={droppable.setNodeRef}
      role="group"
      aria-label={`Group of ${comments.length} cards`}
      className={clsx(
        'group transition',
        droppable.isOver && 'ring-2 ring-brand-500 ring-offset-2',
      )}
    >
      <div className="group-h">
        <Link2 size={13} aria-hidden />
        <span className="badge">{comments.length}</span>
        {canGroup ? (
          <input
            defaultValue={group.title}
            key={group.title}
            placeholder="Name this group"
            maxLength={GROUP_TITLE_MAX_LENGTH}
            aria-label="Group name"
            onBlur={(event) => {
              const value = event.target.value.trim();
              if (value !== group.title) void send('group:rename', { groupId: group.id, title: value });
            }}
            className="min-w-0 flex-1 bg-transparent font-bold placeholder:font-normal placeholder:text-muted focus:outline-none"
          />
        ) : (
          <span
            className="min-w-0 flex-1 line-clamp-2 font-bold"
            title={group.title || 'Grouped comments'}
          >
            {group.title || 'Grouped comments'}
          </span>
        )}
      </div>

      {/* Group reactions get their own row, inside the grey wrapper and above
          the cards, so they read as the group's rather than the first card's.
          The row being on top also means the palette opens downward, onto the
          group's own cards — upward it would leave the group's top edge. */}
      <div className="relative mb-2 flex flex-wrap items-center gap-1.5 px-1">
        <ReactionBar
          reactions={group.reactions}
          canReact={canReact}
          myName={myName}
          onToggle={(emoji) => void send('group:toggleReaction', { groupId: group.id, emoji })}
          placement="below"
        />
      </div>

      <div className="space-y-2">
        {comments.map((comment) => (
          <DraggableCard
            key={comment.id}
            comment={comment}
            topics={topics}
            canGroup={canGroup}
            canReact={canReact}
            inGroup
            dimmed={dimmedIds ? !dimmedIds.has(comment.id) : false}
            highlighted={highlighted}
            myName={myName}
            onHighlightAuthor={() => onHighlightAuthor(comment)}
            onGroupWith={onGroupWith ? () => onGroupWith(comment) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Step 2. Everything is revealed; the work is condensing duplicates into groups
 * and tagging themes so the vote step has fewer, clearer options.
 */
export function GroupPhase({ snapshot }: { snapshot: RetroSnapshot }) {
  const send = useRetroStore((state) => state.send);
  const { settings } = snapshot.retro;
  const isFacilitator = snapshot.me.isFacilitator;

  const canGroup = !settings.group.restrictGroupingToFacilitators || isFacilitator;
  const canFilter = !settings.group.restrictFilteringToFacilitators || isFacilitator;
  const canSort = !settings.group.restrictSortingToFacilitators || isFacilitator;
  const canHighlight = !settings.group.restrictHighlightingToFacilitators || isFacilitator;
  const canReact = settings.group.allowReactions;
  const canAddComments = settings.group.allowNewComments || isFacilitator;

  const [memberFilter, setMemberFilter] = useState<string>('ALL');
  const [sort, setSort] = useState<SortMode>('NONE');
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [dragging, setDragging] = useState<CommentView | null>(null);
  const [groupingSource, setGroupingSource] = useState<CommentView | null>(null);

  // Mouse and touch get separate sensors on purpose. A PointerSensor would
  // handle both, but then its distance activation fires on the first few pixels
  // of a *touch scroll* — the finger moves, a drag starts, and the board
  // flickers. Splitting them lets touch keep the press-and-hold delay (so a
  // plain swipe scrolls) while the mouse stays on immediate distance dragging.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );

  const sortComments = (list: CommentView[]): CommentView[] => {
    const copy = [...list];
    switch (sort) {
      case 'REACTIONS':
        return copy.sort(
          (a, b) =>
            b.reactions.reduce((sum, r) => sum + r.count, 0) -
              a.reactions.reduce((sum, r) => sum + r.count, 0) || a.order - b.order,
        );
      case 'NEWEST':
        return copy.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      case 'OLDEST':
        return copy.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      default:
        return copy.sort((a, b) => a.order - b.order);
    }
  };

  /**
   * Filtering hides cards; highlighting dims them. Dimming is better when
   * someone is introducing their own points, because the board keeps its shape.
   */
  const visibleIds = useMemo(() => {
    if (memberFilter === 'ALL') return null;
    return new Set(
      snapshot.comments
        .filter((comment) => comment.author?.participantId === memberFilter)
        .map((comment) => comment.id),
    );
  }, [memberFilter, snapshot.comments]);

  const highlightIds = useMemo(() => {
    if (!highlighted) return null;
    return new Set(
      snapshot.comments
        .filter((comment) => comment.author?.participantId === highlighted)
        .map((comment) => comment.id),
    );
  }, [highlighted, snapshot.comments]);

  // Clicking the same name again clears the highlight: the control that turned it
  // on is the obvious one to turn it off, and the toolbar button is easy to miss.
  function toggleHighlight(comment: CommentView) {
    if (!canHighlight) return;
    const id = comment.author?.participantId ?? null;
    setHighlighted((current) => (current === id ? null : id));
  }

  function onDragStart(event: DragStartEvent) {
    const parsed = parseDropId(String(event.active.id));
    if (parsed?.type === 'COMMENT') {
      setDragging(snapshot.comments.find((comment) => comment.id === parsed.id) ?? null);
    }
  }

  function onDragEnd(event: DragEndEvent) {
    setDragging(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const source = parseDropId(String(active.id));
    const target = parseDropId(String(over.id));
    if (!source || source.type !== 'COMMENT' || !target) return;

    void send('comment:group', { commentId: source.id, target });
  }

  const namedParticipants = snapshot.participants.filter((participant) =>
    snapshot.comments.some((comment) => comment.author?.participantId === participant.id),
  );

  return (
    <DndContext
      sensors={sensors}
      // Resolve the drop target from the pointer position, not from the dragged
      // card's rectangle. The default (rectIntersection) makes a tall card at the
      // bottom of a column unable to reach a short card just above it — its own
      // rectangle overlaps everything but the small target. pointerWithin keys
      // off where the finger/cursor actually is, so any card under it is reachable.
      collisionDetection={pointerWithin}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setDragging(null)}
    >
      <div className="filters">
        {canFilter ? (
          <select
            className="input input-compact w-auto"
            value={memberFilter}
            onChange={(event) => setMemberFilter(event.target.value)}
            aria-label="Filter by member"
          >
            <option value="ALL">Everyone’s comments</option>
            {namedParticipants.map((participant) => (
              <option key={participant.id} value={participant.id}>
                {participant.name}
              </option>
            ))}
          </select>
        ) : null}

        {canSort ? (
          <select
            className="input input-compact w-auto"
            value={sort}
            onChange={(event) => setSort(event.target.value as SortMode)}
            aria-label="Sort comments"
          >
            {Object.entries(SORT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        ) : null}

        {highlighted ? (
          <button type="button" className="btn btn-sm" onClick={() => setHighlighted(null)}>
            Clear highlight
          </button>
        ) : null}

        {canGroup ? (
          <p className="ml-auto hidden text-[12px] text-muted lg:block">
            Drag a card onto another to group them.
          </p>
        ) : (
          <p className="ml-auto text-[12px] text-muted">The facilitator is grouping comments.</p>
        )}
      </div>

      <BoardTrack>
        {snapshot.columns.map((column, index) => {
          const columnComments = snapshot.comments.filter((comment) => comment.columnId === column.id);
          const groups = snapshot.groups
            .filter((group) => group.columnId === column.id)
            .sort((a, b) => a.order - b.order);

          const ungrouped = sortComments(columnComments.filter((comment) => !comment.groupId)).filter(
            (comment) => !visibleIds || visibleIds.has(comment.id),
          );

          const pendingSuggestions = snapshot.suggestions.filter((suggestion) =>
            suggestion.commentIds.some((id) =>
              columnComments.some((comment) => comment.id === id && !comment.groupId),
            ),
          );

          const suggestedIds = new Set(pendingSuggestions.flatMap((s) => s.commentIds));

          return (
            <BoardColumn
              key={column.id}
              column={column}
              index={index}
              badge={<span className="badge">{columnComments.length}</span>}
              action={
                <CommentComposer
                  columnId={column.id}
                  disabled={!canAddComments}
                  disabledReason="The board is closed to new comments."
                />
              }
            >
              {pendingSuggestions.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  comments={columnComments}
                  topics={snapshot.topics}
                />
              ))}

              {groups.map((group) => {
                const groupComments = sortComments(
                  columnComments.filter((comment) => comment.groupId === group.id),
                );
                if (visibleIds && !groupComments.some((comment) => visibleIds.has(comment.id))) return null;

                return (
                  <GroupContainer
                    key={group.id}
                    group={group}
                    comments={groupComments}
                    topics={snapshot.topics}
                    canGroup={canGroup}
                    canReact={canReact}
                    dimmedIds={highlightIds}
                    highlighted={highlighted}
                    myName={snapshot.me.name}
                    onHighlightAuthor={toggleHighlight}
                    onGroupWith={canGroup ? setGroupingSource : undefined}
                  />
                );
              })}

              {ungrouped
                .filter((comment) => !suggestedIds.has(comment.id))
                .map((comment) => (
                  <DraggableCard
                    key={comment.id}
                    comment={comment}
                    topics={snapshot.topics}
                    canGroup={canGroup}
                    canReact={canReact}
                    inGroup={false}
                    dimmed={highlightIds ? !highlightIds.has(comment.id) : false}
                    highlighted={highlighted}
                    myName={snapshot.me.name}
                    onHighlightAuthor={() => toggleHighlight(comment)}
                    onGroupWith={canGroup ? () => setGroupingSource(comment) : undefined}
                  />
                ))}
            </BoardColumn>
          );
        })}
      </BoardTrack>

      {/* Follows the finger/cursor so it is obvious what is being dragged. */}
      <DragOverlay dropAnimation={null}>
        {dragging ? (
          <div className="w-72 rotate-2">
            <CommentCard
              comment={dragging}
              topics={snapshot.topics}
              myName={snapshot.me.name}
              className="shadow-panel"
            />
          </div>
        ) : null}
      </DragOverlay>

      <GroupWithDialog
        open={groupingSource !== null}
        onClose={() => setGroupingSource(null)}
        source={groupingSource}
        // Only same-column targets are offered, so an invalid grouping is not
        // something the user can even attempt here.
        candidates={
          groupingSource
            ? snapshot.comments.filter(
                (candidate) =>
                  candidate.columnId === groupingSource.columnId &&
                  candidate.id !== groupingSource.id &&
                  !candidate.groupId,
              )
            : []
        }
        groups={
          groupingSource
            ? snapshot.groups.filter(
                (group) =>
                  group.columnId === groupingSource.columnId && group.id !== groupingSource.groupId,
              )
            : []
        }
      />
    </DndContext>
  );
}
